import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import http from '../../api/http';
import { Container, Paper, Box, Typography, Alert, TextField, Button } from '@mui/material';

interface ErrorResponse {
  response?: {
    data?: {
      msg?: string;
      message?: string;
      errors?: string[];
      error?: string;
      details?: string[];
    };
  };
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [err, setErr] = useState('');
  const [errorList, setErrorList] = useState<string[]>([]);
  const [errorDetail, setErrorDetail] = useState('');
  const [detailsList, setDetailsList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setErrorList([]);
    setErrorDetail('');
    setDetailsList([]);
    setErrors({});
    setIsLoading(true);
    try {
      await http.post('/api/users/auth/reset-password', {
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });
      setSuccess(true);
    } catch (error: any) {
      const typedError = error as ErrorResponse;
      const apiMsg = typedError?.response?.data?.message;
      const apiErrors = typedError?.response?.data?.errors;
      const apiErrorDetail = typedError?.response?.data?.error;
      const apiDetails = typedError?.response?.data?.details;
      setErr(apiMsg || 'Password reset failed');
      setErrorList(Array.isArray(apiErrors) ? apiErrors : []);
      setErrorDetail(apiErrorDetail || '');
      setDetailsList(Array.isArray(apiDetails) ? apiDetails : []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'rgb(247, 244, 244)', py: 8 }}>
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" mb={2}>
              Reset Password
            </Typography>
            {success ? (
              <Box textAlign="center">
                <Alert severity="success" sx={{ mb: 2 }}>
                  Your password has been reset successfully!
                </Alert>
                <Button variant="contained" onClick={() => navigate('/login')}>
                  Sign in
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                {err && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {err}
                    {errorDetail && (
                      <div style={{ fontSize: 13, color: '#b71c1c', marginTop: 4 }}>{errorDetail}</div>
                    )}
                    {errorList.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errorList.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    )}
                    {detailsList.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {detailsList.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                )}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="New Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors['password']}
                  helperText={errors['password']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors['confirmPassword']}
                  helperText={errors['confirmPassword']}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword; 